'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import useLocalStorage from '@alltrails/local-storage/useLocalStorage';
import type DeeplinkData from '@alltrails/types/DeeplinkData';

const SignUpModal = dynamic(() => import('../SignUpModal').then(module => module));

const scrollLimitKey = 'scroll-limit-reached';
const pageLimitReached = 'page-limit-reached';

/* 
  Triggers the signup modal when a scroll threshold is reached.
*/
const ScrollLimit = ({ deeplinkData }: { deeplinkData?: DeeplinkData }) => {
  const { getValue: getScrollLimitReached, setValue: setScrollLimitReached } = useLocalStorage<boolean>(scrollLimitKey, false);
  const { getValue: getPageLimitReached, setValue: setPageLimitReached } = useLocalStorage<boolean>(pageLimitReached, false);

  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const didLoginRef = useRef(false);
  const user = useUser();
  const disableScrollListenerRef = useRef(false);

  useEffect(() => {
    if (user) {
      return;
    }

    // Disable scroll restoration behavior to prevent scroll limit loop.
    if (history.scrollRestoration) {
      history.scrollRestoration = 'manual';
    }

    if (isSignupModalOpen) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }

    const scrollThreshold = window.innerHeight;
    const onScroll = () => {
      if (disableScrollListenerRef.current) {
        return;
      }

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const thresholdReached = scrollTop >= scrollThreshold;

      if (!isSignupModalOpen && thresholdReached) {
        if (!getScrollLimitReached()) {
          setScrollLimitReached(true);
        }
        setIsSignupModalOpen(true);
      }

      if (isSignupModalOpen && !thresholdReached) {
        setIsSignupModalOpen(false);
      }
    };

    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [getScrollLimitReached, isSignupModalOpen, setScrollLimitReached, user]);

  useEffect(() => {
    if (!getPageLimitReached()) {
      setPageLimitReached(true);
    }
    // Don't include getPageLimitReached so that it only runs once on mount.
  }, [user]);

  const handleCloseModal = useCallback(() => {
    // Disable the scroll listener when autoscrolling to the top of the page.
    if (!didLoginRef.current) {
      disableScrollListenerRef.current = true;
      window.scrollTo(0, 0);
      setTimeout(() => {
        disableScrollListenerRef.current = false;
      }, 2000);
    }
    setIsSignupModalOpen(false);
  }, [user]);

  return (
    <>
      <SignUpModal
        autoClose
        deeplinkData={deeplinkData}
        trigger="scroll auth"
        isOpen={isSignupModalOpen}
        closeModal={handleCloseModal}
        onSuccess={() => {
          didLoginRef.current = true;
          setIsSignupModalOpen(false);
        }}
        upsellTrigger="scroll auth"
      />
    </>
  );
};

export default ScrollLimit;
